import type { TabularTreePrimaryRowProps } from '@ztechapps/webui.data-tools'
import type { Product, ProductBillOfMaterial } from '@ztechapps/common.contract'
import { CellWithContextMenu } from '@ztechapps/webui.menu'
import { RowContextMenu } from './RowContextMenu'
import { HorizontalStack } from '@ztechapps/webui.util'
import { BomProductWidget } from './BomProductWidget'
import React from 'react'
import { updateProduct_billOfMaterials_with_parent_product } from './graphql'
import { CheckboxInlineInput } from '@ztechapps/webui.input'
import { ShowIf } from '@ztechapps/webui.util'
import { formatMoney } from '@ztechapps/common.utils'
import { QuantityWithUom, QuantityWithUomInlineInput } from '../QuantityWithUom'
import { useBillOfMaterialsContext } from './Context'
import { BomComponentCostOverlay } from './BomComponentCostOverlay'

export const BomRow = ({
  leftMarginStyle,
  item,
  parent,
  toggleShowChildren,
  toggleShowNew,
  children,
  root,
}: TabularTreePrimaryRowProps<ProductBillOfMaterial>) => {
  const bomId = item._id
  const bomProduct = item?.product as Product
  const parentProductId = parent?.product._id

  const { components, showInventoryFields, canEdit, rowHighlight } = useBillOfMaterialsContext()
  const { _id, runningInventory, sharedInventory } = item
  const mainProductId = root?.product?._id
  const ProductInlineInput = components.inlineInput

  const rowBg = (currentItem: ProductBillOfMaterial) => {
    let rowClassName = ''
    if (rowHighlight) {
      const bomProduct = currentItem.product as Product
      const requiredToBuild = currentItem.requiredToBuild
      //if we need row highlight, only do this
      const needMoreUnit = bomProduct.requiredComputedQuantityOnHand > 0

      if (needMoreUnit) {
        rowClassName = 'table-danger'
      } else if (!requiredToBuild) {
        rowClassName = 'table-warning'
      } else {
        rowClassName = ''
      }
    }

    return rowClassName
  }

  return (
    <tr className={rowBg(item)}>
      <CellWithContextMenu
        contextMenuContent={
          <RowContextMenu
            bomId={bomId}
            productId={parentProductId}
            showExtensionToggle={toggleShowChildren}
            showAddNewToggle={toggleShowNew}
          />
        }
      >
        <HorizontalStack gap={1} style={leftMarginStyle}>
          {children}
          <BomProductWidget {...bomProduct} />
        </HorizontalStack>
      </CellWithContextMenu>

      <td className={'text-end'}>
        <ProductInlineInput
          mutationConfig={{
            query: updateProduct_billOfMaterials_with_parent_product,
          }}
          canEdit={canEdit}
          parentInputComponent={QuantityWithUomInlineInput}
          name={`quantity`}
          initialValues={item}
          inputProps={{
            symbol: bomProduct?.workOrderUom?.symbol,
            decimalPrecision: bomProduct?.unitOfMeasurePrecision ?? 0,
          }}
          updateParams={({ updatedValue }) => {
            const result = {
              input: {
                quantity: updatedValue,
                product: { _id: bomProduct._id },
              },
              filter: {
                parentProductId: mainProductId,
                _id: parentProductId,
                'billOfMaterials._id': _id,
              },
            }
            return result
          }}
        />
      </td>
      <td className={'text-end'}>
        <ProductInlineInput
          mutationConfig={{
            query: updateProduct_billOfMaterials_with_parent_product,
          }}
          displayContent={(value) => (value ? 'YES' : 'NO')}
          canEdit={canEdit}
          parentInputComponent={CheckboxInlineInput}
          name={`requiredToBuild`}
          initialValues={item}
          updateParams={({ updatedValue }) => {
            const result = {
              input: {
                requiredToBuild: updatedValue,
                product: { _id: bomProduct._id },
              },
              filter: {
                parentProductId: mainProductId,
                _id: parentProductId,
                'billOfMaterials._id': _id,
              },
            }
            return result
          }}
        />
      </td>

      <ShowIf condition={showInventoryFields}>
        <td className={'text-end'}>
          <QuantityWithUom
            quantity={bomProduct.requiredComputedQuantityOnHand}
            symbol={bomProduct?.workOrderUom?.symbol}
            decimalPrecision={bomProduct?.unitOfMeasurePrecision ?? 0}
          />
        </td>
        <td className={'text-end'}>
          <QuantityWithUom
            quantity={sharedInventory}
            symbol={bomProduct?.workOrderUom?.symbol}
            decimalPrecision={bomProduct?.unitOfMeasurePrecision ?? 0}
          />
        </td>
        <td className={'text-end'}>
          <QuantityWithUom
            quantity={runningInventory}
            symbol={bomProduct?.workOrderUom?.symbol}
            decimalPrecision={bomProduct?.unitOfMeasurePrecision ?? 0}
          />
        </td>
      </ShowIf>

      <td className={'text-end'}>
        {formatMoney(bomProduct?.totalManufactureCost)}
        <BomComponentCostOverlay product={bomProduct} />
      </td>
    </tr>
  )
}
